/** subscriptions make free and premium download permissions a little more complicated.
 *  this is just a facade over that to:
 *  - put the permissions info into one var
 *  - make it more human-readable
 */
import ddt from '@wix/da-ddt';
import { PapiDeviation, PapiExtendedDeviation } from '@wix/da-papi-types';

const logger = ddt.createLogger('downloadbutton');

// -------------------------- free downloads
type FreeDownloadData = PapiExtendedDeviation['download'];

type FreeDownloadPermission =
  | undefined
  | 'downloadable'
  | 'needs_subscription'
  | 'blocked_by_subscription';

export const FREE_DOWNLOAD_PERMISSIONS = {
  DOWNLOADABLE: 'downloadable',
  NEEDS_SUBSCRIPTION: 'needs_subscription',
  BLOCKED_BY_SUBSCRIPTION: 'blocked_by_subscription',
} as Record<string, FreeDownloadPermission>;

export function getDeviationFreeDownload(deviation: PapiDeviation): {
  download?: FreeDownloadData;
  permission?: FreeDownloadPermission;
} {
  const freeDownload = deviation.extended?.download;
  logger.log('free download', freeDownload);
  if (!freeDownload) {
    return {};
  }

  let permission: FreeDownloadPermission = FREE_DOWNLOAD_PERMISSIONS.DOWNLOADABLE;

  // the only thing preventing a free download is a subscription
  const tierAccess = deviation.tierAccess;
  if (tierAccess === 'locked') {
    permission = FREE_DOWNLOAD_PERMISSIONS.NEEDS_SUBSCRIPTION;
  }
  if (tierAccess === 'locked-subscribed') {
    permission = FREE_DOWNLOAD_PERMISSIONS.BLOCKED_BY_SUBSCRIPTION;
  }

  logger.log('free download', { tierAccess, permission });
  return { download: freeDownload, permission };
}

// -------------------------- premium downloads
type PremiumDownloadData = PapiExtendedDeviation['pcp'];

type PremiumDownloadPermission = undefined | 'downloadable' | 'purchasable';

export const PREMIUM_DOWNLOAD_PERMISSIONS = {
  DOWNLOADABLE: 'downloadable',
  PURCHASABLE: 'purchasable',
} as Record<string, PremiumDownloadPermission>;

export function getDeviationPremiumDownload(
  deviation: PapiDeviation,
  isOwner?: boolean
): {
  download?: PremiumDownloadData;
  permission?: PremiumDownloadPermission;
} {
  const premiumDownload = deviation.extended?.pcp;
  logger.log('premium download', premiumDownload);
  if (!premiumDownload) {
    return {};
  }

  // purchasing, unblocked subscriptions, and ownership
  // give access to premium downloads
  const purchasedSeparately = premiumDownload?.hasUserPurchased;
  const tierAccess = deviation.tierAccess;
  const hasSubscriptionAccess = tierAccess === 'unlocked';
  const hasPremiumAccess = purchasedSeparately || hasSubscriptionAccess || isOwner;

  const permission: PremiumDownloadPermission = hasPremiumAccess
    ? PREMIUM_DOWNLOAD_PERMISSIONS.DOWNLOADABLE
    : PREMIUM_DOWNLOAD_PERMISSIONS.PURCHASABLE;

  logger.log('premium download', {
    isOwner,
    purchasedSeparately,
    tierAccess,
    permission,
  });
  return { download: premiumDownload, permission };
}
